import type { BadgeColor } from '@customerio/pluma-components/react';
import type { SQLImportRow as UIAPISQLImportRow } from '@customerio/hydra-ui-api';

/**
 * One run of a SQL import: the query that ran, how many rows it produced and
 * how far Customer.io got ingesting them. Backed by
 * `/v1/environments/{env}/sql_import_rows?sql_import_id=` — the import list does
 * not sideload runs, so they are always a separate read. A relationship import's
 * child runs are folded into the parent's list by the backend, already
 * attributed to the parent `import_id`.
 */
export type SqlImportRow = UIAPISQLImportRow;

export const sqlImportRowKeys = {
  all: (workspaceId: number) => ['workspace', workspaceId, 'sql_import_row'] as const,
  lists: (workspaceId: number) => [...sqlImportRowKeys.all(workspaceId), 'list'] as const,
  list: (workspaceId: number, importId: string) => [...sqlImportRowKeys.lists(workspaceId), importId] as const,
};

/**
 * A run's status. The first seven come off the wire as `status`; `queued`,
 * `processing` and `partiallyFailed` are derived from the row counts (see
 * `sqlImportRowStatusDetails`), because a run the backend calls `done` is only
 * finished once ingress has processed every row it produced.
 */
export const SqlImportRowStatus = {
  querying: 'querying',
  importing: 'importing',
  done: 'done',
  failed: 'failed',
  canceled: 'canceled',
  skipped: 'skipped',
  partiallyFailed: 'partiallyFailed',
  queued: 'queued',
  processing: 'processing',
} as const;
export type SqlImportRowStatus = (typeof SqlImportRowStatus)[keyof typeof SqlImportRowStatus];

export interface SqlImportRowStatusDetails {
  color: BadgeColor | undefined;
  label: string;
  tooltip?: string;
}

export const SQL_IMPORT_ROW_STATUS_DETAILS = {
  querying: { color: undefined, label: 'Preparing', tooltip: 'Querying' },
  importing: { color: undefined, label: 'Preparing', tooltip: 'Transforming data for import into Customer.io' },
  done: { color: 'green', label: 'Complete', tooltip: 'Import successful. Data is ready for use in Customer.io' },
  failed: { color: 'red', label: 'Failed' },
  canceled: { color: undefined, label: 'Canceled' },
  partiallyFailed: { color: 'yellow', label: 'Some rows failed' },
  skipped: { color: 'red', label: 'Skipped' },
  queued: {
    color: 'yellow',
    label: 'Queued',
    tooltip: 'Data will begin importing once other data syncs and imports are complete',
  },
  processing: { color: 'yellow', label: 'In Progress', tooltip: 'Data is being imported into Customer.io' },
} satisfies Record<SqlImportRowStatus, SqlImportRowStatusDetails>;

type RowCounts = Pick<SqlImportRow, 'added_rows' | 'updated_rows' | 'deleted_rows' | 'ingressed_rows'>;
type RowStatus = Pick<SqlImportRow, 'status'>;

/** The rows a run handed to ingress: everything it added, updated or deleted. */
function rowsToImport(row: Pick<RowCounts, 'added_rows' | 'updated_rows' | 'deleted_rows'>): number {
  return row.added_rows + row.updated_rows + row.deleted_rows;
}

export function sqlImportRowIsFailed(row: RowStatus): boolean {
  return row.status === SqlImportRowStatus.failed;
}

export function sqlImportRowIsSkipped(row: RowStatus): boolean {
  return row.status === SqlImportRowStatus.skipped;
}

export function sqlImportRowIsCanceled(row: RowStatus): boolean {
  return row.status === SqlImportRowStatus.canceled;
}

function isQuerying(row: RowStatus): boolean {
  return row.status === SqlImportRowStatus.querying;
}

function isImporting(row: RowStatus): boolean {
  return row.status === SqlImportRowStatus.importing;
}

/** Really done: the backend finished and ingress has processed every produced row. */
export function sqlImportRowIsDone(row: RowStatus & RowCounts): boolean {
  return row.status === SqlImportRowStatus.done && row.ingressed_rows >= rowsToImport(row);
}

/** Ingress has started on the produced rows but not finished them. */
export function sqlImportRowIsProcessing(row: RowStatus & RowCounts): boolean {
  if (row.status !== SqlImportRowStatus.done) {
    return false;
  }
  const total = rowsToImport(row);
  return total > 0 && row.ingressed_rows > 0 && row.ingressed_rows < total;
}

/** The backend finished but ingress has not picked the rows up yet. */
export function sqlImportRowIsQueued(row: RowStatus & RowCounts): boolean {
  return row.status === SqlImportRowStatus.done && rowsToImport(row) > 0 && row.ingressed_rows === 0;
}

/** Still moving — querying, transforming, queued for ingress, or mid-ingress. Cancelable while true. */
export function sqlImportRowIsPending(row: RowStatus & RowCounts): boolean {
  return isQuerying(row) || isImporting(row) || sqlImportRowIsQueued(row) || sqlImportRowIsProcessing(row);
}

/** Failed, but some rows made it in first. */
export function sqlImportRowIsPartiallyFailed(
  row: RowStatus & Pick<RowCounts, 'added_rows' | 'updated_rows'>,
): boolean {
  return sqlImportRowIsFailed(row) && (row.added_rows > 0 || row.updated_rows > 0);
}

/** A failed run wrote its rejected rows to a downloadable export. */
export function sqlImportRowHasErrorFile(row: Pick<SqlImportRow, 'error_export_id'>): boolean {
  return row.error_export_id > 0;
}

/**
 * The status to show for a run, with the derived statuses resolved and the
 * in-progress tooltip carrying the ingress count.
 */
export function sqlImportRowStatusDetails(row: RowStatus & RowCounts): SqlImportRowStatusDetails {
  if (sqlImportRowIsPartiallyFailed(row)) {
    return SQL_IMPORT_ROW_STATUS_DETAILS.partiallyFailed;
  }
  if (sqlImportRowIsFailed(row)) {
    return SQL_IMPORT_ROW_STATUS_DETAILS.failed;
  }
  if (sqlImportRowIsQueued(row)) {
    return SQL_IMPORT_ROW_STATUS_DETAILS.queued;
  }
  if (sqlImportRowIsProcessing(row)) {
    return {
      ...SQL_IMPORT_ROW_STATUS_DETAILS.processing,
      tooltip: `${row.ingressed_rows} of ${rowsToImport(row)} rows imported into Customer.io`,
    };
  }
  return statusDetailsFor(row.status);
}

const DETAILS_BY_STATUS = new Map(Object.entries(SQL_IMPORT_ROW_STATUS_DETAILS));

/** The wire `status` is an open string; anything unrecognized reads as still preparing. */
function statusDetailsFor(status: string): SqlImportRowStatusDetails {
  return DETAILS_BY_STATUS.get(status) ?? SQL_IMPORT_ROW_STATUS_DETAILS.querying;
}
